"use client"

import AssetComboIcon from "@src/components/DefuseSDK/components/Asset/AssetComboIcon"
import { nearClient } from "@src/components/DefuseSDK/constants/nearClient"
import { invalidatePrivateBalanceQueries } from "@src/components/DefuseSDK/features/account/queries/privateBalanceQueries"
import {
  DEPOSIT_STAGES,
  type DepositDisplayStage,
} from "@src/components/DefuseSDK/features/deposit/utils/depositStatusUtils"
import {
  type ConfidentialDepositOutcome,
  classifyConfidentialStatus,
} from "@src/components/DefuseSDK/features/machines/oneClickStatusMachine"
import { fetchConfidentialExecutionStatus } from "@src/components/DefuseSDK/features/machines/privateIntents"
import { invalidateAllBalanceQueries } from "@src/components/DefuseSDK/queries/balanceQueries"
import { getDepositedBalances } from "@src/components/DefuseSDK/services/defuseBalanceService"
import type { BaseTokenInfo } from "@src/components/DefuseSDK/types/base"
import type { IntentsUserId } from "@src/components/DefuseSDK/types/intentsUserId"
import { ProgressSteps } from "@src/components/ProgressIndicator"
import { logger } from "@src/utils/logger"
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react"
import { useActivityDock } from "./ActivityDockProvider"

const POLL_INTERVAL = 3000
const ACTIVE_DEPOSIT_TIMEOUT = 30 * 60 * 1000 // 30 minutes
const PASSIVE_DEPOSIT_TIMEOUT = 2 * 60 * 1000 // 2 minutes

export type RegisterDepositParams = {
  depositId: string
  userId: IntentsUserId
  defuseAssetId: BaseTokenInfo["defuseAssetId"]
  snapshotBalance: bigint | null
  token: { symbol: string; icon: string }
  chainName?: string
  formattedAmount: string
  explorerUrl?: string | null
  stageLabels: Record<DepositDisplayStage, string>
  skipDock?: boolean
  onArrival?: (delta: bigint) => void
  onTimeout?: () => void
}

export type WatchPassiveDepositParams = {
  userId: IntentsUserId
  defuseAssetId: BaseTokenInfo["defuseAssetId"]
  onArrival?: (delta: bigint) => void
  onTimeout?: () => void
}

// Confidential active deposits can't be tracked by balance delta (balance is
// encrypted), so we poll the 1CS execution status by depositAddress instead.
export type TrackConfidentialDepositParams = {
  depositId: string
  depositAddress: string
  depositMemo?: string | null
  token: { symbol: string; icon: string }
  chainName?: string
  formattedAmount: string
  explorerUrl?: string | null
  stageLabels: Record<DepositDisplayStage, string>
  onArrival?: () => void
  onTimeout?: () => void
}

type DepositTrackerContextType = {
  registerDeposit: (params: RegisterDepositParams) => void
  trackConfidentialDeposit: (params: TrackConfidentialDepositParams) => void
  watchPassiveDeposit: (params: WatchPassiveDepositParams) => void
  stopPassiveWatch: (defuseAssetId: string) => void
}

const DepositTrackerContext = createContext<
  DepositTrackerContextType | undefined
>(undefined)

export function DepositTrackerProvider({ children }: { children: ReactNode }) {
  const { addDockItem, updateDockItem, settleDockItem, hasDockItem } =
    useActivityDock()

  const passiveAbortRefs = useRef<Map<string, AbortController>>(new Map())
  const confidentialAbortRefs = useRef<Map<string, AbortController>>(new Map())

  const registerDeposit = useCallback(
    (params: RegisterDepositParams) => {
      const dockId = `deposit-${params.depositId}`
      if (hasDockItem(dockId)) return

      if (!params.skipDock) {
        addDockItem({
          id: dockId,
          title: `Receive ${params.formattedAmount} ${params.token.symbol}`,
          icons: [
            <AssetComboIcon
              key="token"
              sizeClassName="size-7"
              icon={params.token.icon}
              chainName={params.chainName}
            />,
          ],
          explorerUrl: params.explorerUrl ?? undefined,
          keyValueRows: [],
          renderContent: () => (
            <ProgressSteps
              stages={DEPOSIT_STAGES}
              stageLabels={params.stageLabels}
              displayStage="crediting"
              displayIndex={2}
              isError={false}
              isSuccess={false}
              size="sm"
            />
          ),
        })
      }

      void trackDepositArrival({
        ...params,
        timeout: ACTIVE_DEPOSIT_TIMEOUT,
        dockId,
        updateDockItem,
        settleDockItem,
      }).catch((error) => {
        logger.error(
          "Deposit tracker: unexpected error in trackDepositArrival",
          { depositId: params.depositId, error: String(error) }
        )
      })
    },
    [addDockItem, hasDockItem, updateDockItem, settleDockItem]
  )

  const trackConfidentialDeposit = useCallback(
    (params: TrackConfidentialDepositParams) => {
      const dockId = `deposit-${params.depositId}`
      if (hasDockItem(dockId)) return

      addDockItem({
        id: dockId,
        title: `Receive ${params.formattedAmount} ${params.token.symbol}`,
        icons: [
          <AssetComboIcon
            key="token"
            sizeClassName="size-7"
            icon={params.token.icon}
            chainName={params.chainName}
          />,
        ],
        explorerUrl: params.explorerUrl ?? undefined,
        keyValueRows: [],
        renderContent: () => (
          <ProgressSteps
            stages={DEPOSIT_STAGES}
            stageLabels={params.stageLabels}
            displayStage="crediting"
            displayIndex={2}
            isError={false}
            isSuccess={false}
            size="sm"
          />
        ),
      })

      const existing = confidentialAbortRefs.current.get(params.depositId)
      if (existing) existing.abort()

      const controller = new AbortController()
      confidentialAbortRefs.current.set(params.depositId, controller)

      void trackConfidentialDepositSettlement({
        ...params,
        timeout: ACTIVE_DEPOSIT_TIMEOUT,
        dockId,
        signal: controller.signal,
        updateDockItem,
        settleDockItem,
      })
        .catch((error) => {
          logger.error(
            "Deposit tracker: unexpected error in trackConfidentialDeposit",
            { depositId: params.depositId, error: String(error) }
          )
        })
        .finally(() => {
          if (
            confidentialAbortRefs.current.get(params.depositId) === controller
          ) {
            confidentialAbortRefs.current.delete(params.depositId)
          }
        })
    },
    [addDockItem, hasDockItem, updateDockItem, settleDockItem]
  )

  const watchPassiveDeposit = useCallback(
    ({
      userId,
      defuseAssetId,
      onArrival,
      onTimeout,
    }: WatchPassiveDepositParams) => {
      const existing = passiveAbortRefs.current.get(defuseAssetId)
      if (existing) existing.abort()

      const controller = new AbortController()
      passiveAbortRefs.current.set(defuseAssetId, controller)

      void pollForArrival({
        userId,
        defuseAssetId,
        snapshotBalance: null,
        timeout: PASSIVE_DEPOSIT_TIMEOUT,
        signal: controller.signal,
      })
        .then((result) => {
          if (passiveAbortRefs.current.get(defuseAssetId) === controller) {
            passiveAbortRefs.current.delete(defuseAssetId)
          }

          if (!result) {
            if (!controller.signal.aborted) {
              onTimeout?.()
            }
            return
          }

          invalidateAllBalanceQueries()
          onArrival?.(result.delta)
        })
        .catch((error) => {
          if (passiveAbortRefs.current.get(defuseAssetId) === controller) {
            passiveAbortRefs.current.delete(defuseAssetId)
          }
          logger.error("Deposit tracker: unexpected error in passive watch", {
            defuseAssetId,
            error: String(error),
          })
        })
    },
    []
  )

  const stopPassiveWatch = useCallback((defuseAssetId: string) => {
    const controller = passiveAbortRefs.current.get(defuseAssetId)
    if (controller) {
      controller.abort()
      passiveAbortRefs.current.delete(defuseAssetId)
    }
  }, [])

  return (
    <DepositTrackerContext.Provider
      value={useMemo(
        () => ({
          registerDeposit,
          trackConfidentialDeposit,
          watchPassiveDeposit,
          stopPassiveWatch,
        }),
        [
          registerDeposit,
          trackConfidentialDeposit,
          watchPassiveDeposit,
          stopPassiveWatch,
        ]
      )}
    >
      {children}
    </DepositTrackerContext.Provider>
  )
}

export function useDepositTracker() {
  const context = useContext(DepositTrackerContext)

  if (!context) {
    throw new Error(
      "useDepositTracker must be used within a DepositTrackerProvider"
    )
  }

  return context
}

type ArrivalResult = { delta: bigint; currentBalance: bigint }

async function pollForArrival({
  userId,
  defuseAssetId,
  snapshotBalance,
  timeout,
  signal,
}: {
  userId: IntentsUserId
  defuseAssetId: string
  snapshotBalance: bigint | null
  timeout: number
  signal?: AbortSignal
}): Promise<ArrivalResult | null> {
  const maxPolls = Math.ceil(timeout / POLL_INTERVAL)
  let baseline = snapshotBalance

  for (let attempt = 0; attempt < maxPolls; attempt += 1) {
    if (signal?.aborted) return null

    try {
      const balances = await getDepositedBalances(
        userId,
        [defuseAssetId],
        nearClient
      )
      const currentBalance = balances[defuseAssetId] ?? 0n

      if (baseline == null) {
        baseline = currentBalance
        await sleep(POLL_INTERVAL)
        continue
      }

      const delta = currentBalance - baseline
      if (delta > 0n) {
        logger.info("Deposit tracker: arrival detected", {
          defuseAssetId,
          delta: String(delta),
        })
        return { delta, currentBalance }
      }
    } catch (error) {
      logger.warn("Deposit tracker: poll failed, retrying", {
        defuseAssetId,
        error: String(error),
      })
    }

    await sleep(POLL_INTERVAL)
  }

  return null
}

async function trackDepositArrival({
  userId,
  defuseAssetId,
  snapshotBalance,
  stageLabels,
  timeout,
  dockId,
  onArrival,
  onTimeout,
  updateDockItem,
  settleDockItem,
}: RegisterDepositParams & {
  timeout: number
  dockId: string
  updateDockItem: (
    id: string,
    updates: { renderContent?: () => ReactNode; explorerUrl?: string }
  ) => void
  settleDockItem: (id: string) => void
}) {
  const result = await pollForArrival({
    userId,
    defuseAssetId,
    snapshotBalance,
    timeout,
  })

  if (result) {
    onArrival?.(result.delta)

    updateDockItem(dockId, {
      renderContent: () => (
        <ProgressSteps
          stages={DEPOSIT_STAGES}
          stageLabels={stageLabels}
          displayStage="complete"
          displayIndex={3}
          isError={false}
          isSuccess={true}
          size="sm"
        />
      ),
    })
    settleDockItem(dockId)
    invalidateAllBalanceQueries()
  } else {
    logger.warn("Deposit tracker: timed out", { defuseAssetId })

    onTimeout?.()

    updateDockItem(dockId, {
      renderContent: () => (
        <ProgressSteps
          stages={DEPOSIT_STAGES}
          stageLabels={stageLabels}
          displayStage="complete"
          displayIndex={3}
          isError={true}
          isSuccess={false}
          size="sm"
        />
      ),
    })
    settleDockItem(dockId)
  }
}

async function pollConfidentialStatus({
  depositAddress,
  depositMemo,
  timeout,
  signal,
}: {
  depositAddress: string
  depositMemo?: string | null
  timeout: number
  signal?: AbortSignal
}): Promise<ConfidentialDepositOutcome | null> {
  const maxPolls = Math.ceil(timeout / POLL_INTERVAL)

  for (let attempt = 0; attempt < maxPolls; attempt += 1) {
    if (signal?.aborted) return null

    try {
      const result = await fetchConfidentialExecutionStatus(
        depositAddress,
        depositMemo
      )
      if (!("err" in result)) {
        const outcome = classifyConfidentialStatus(result.ok.status)
        if (outcome !== "pending") return outcome
      }
    } catch (error) {
      logger.warn(
        "Deposit tracker: confidential status poll failed, retrying",
        {
          depositAddress,
          error: String(error),
        }
      )
    }

    await sleep(POLL_INTERVAL)
  }

  return null
}

async function trackConfidentialDepositSettlement({
  depositAddress,
  depositMemo,
  stageLabels,
  timeout,
  dockId,
  signal,
  onArrival,
  onTimeout,
  updateDockItem,
  settleDockItem,
}: TrackConfidentialDepositParams & {
  timeout: number
  dockId: string
  signal?: AbortSignal
  updateDockItem: (
    id: string,
    updates: { renderContent?: () => ReactNode; explorerUrl?: string }
  ) => void
  settleDockItem: (id: string) => void
}) {
  const outcome = await pollConfidentialStatus({
    depositAddress,
    depositMemo,
    timeout,
    signal,
  })

  if (signal?.aborted) return

  const isRefunded = outcome === "refunded"
  // Refund credits the confidential balance, so it settles like success (matches inline UI).
  const settledOk = outcome === "success" || isRefunded

  if (outcome == null) {
    logger.warn("Deposit tracker: confidential deposit timed out", {
      depositAddress,
    })
    onTimeout?.()
  } else if (settledOk) {
    onArrival?.()
  }

  const labels = isRefunded
    ? { ...stageLabels, complete: "Refunded to confidential balance" }
    : stageLabels

  updateDockItem(dockId, {
    renderContent: () => (
      <ProgressSteps
        stages={DEPOSIT_STAGES}
        stageLabels={labels}
        displayStage="complete"
        displayIndex={3}
        isError={!settledOk}
        isSuccess={settledOk}
        size="sm"
      />
    ),
  })
  settleDockItem(dockId)

  if (settledOk) {
    invalidatePrivateBalanceQueries()
    invalidateAllBalanceQueries()
  }
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
